"use client";
import { Box, CircularProgress, Grid, useMediaQuery } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
// import InfoImg from "./InfoImg";
import { VeTypography } from "../../../mui";
import { ApplicationFormTabs } from "./ApplicationFormTabs";
import _ from "lodash";
import { SuccessModal } from "../../Modal/successModal";
import WarningSvg from "./WarningSvg";
import { cleanString } from "../../../hoc";
import { SubSection } from "./SubSection";

export function ApplicationForm({
  variables,
  RichText,
  assets,
  DynamicImage,
  getFileTypeImage,
  useQueryData,
  useMutation,
  mutateAction,
  toast,
  useSession,
  submitCurrentStep,
  setSubmitCurrentStep,
  discardStep,
  setDiscardStep,
  projectId,
  setDisableBtn,
  setIsLoading,
  timeZoneDateTimeUser,
  nextClicked,
  setNextClicked,
  prevClicked,
  setPrevClicked,
  setSelectedTab,
  selectedTab,
  useQueryClient,
  projectDetails,
  setProjectDetails,
  checkSaveState,
  setCheckSaveState,
  applicationFormLoading,
  edit = true,
  fromApplicationPage = false,
}: any) {
  const [nextPage, setNextPage] = useState(0);
  const [questionareData, setQuestionareData] = useState<any>();
  const [updateAnswer, setUpdatedAnswers] = useState<any[]>([]);
  const { data: session, update } = useSession();
  const [fetchedQuestionare, setFetchedQuestionare] = useState();
  const [questionMetadata, setQuestionMetadata] = useState([]);
  const [openDiscardModal, setOpenDiscardModal] = useState(false);
  const [saveChangesModal, setSaveChangesModal] = useState(false);

  const queryClient = useQueryClient();
  const data: any = queryClient.getQueryData([
    "questionare",
    "APPLICATION_FORM",
    projectId,
  ]);
  const metaData: any = queryClient.getQueryData([
    "questionareMetaData",
    "APPLICATION_FORM",
    projectId,
  ]);

  const handleCloseDiscardModal = () => setOpenDiscardModal(false);
  const handleCloseChangesModal = () => setSaveChangesModal(false);

  const { mutate: updateProject } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["update-project"],
    onSuccess: (data: any) => {
      if (data.error) {
        throw new Error(data.error?.heading, { cause: data?.error?.message });
      }
      if (setProjectDetails) setProjectDetails(data?.updated_project_data);
      if (variables?.projectRefetch) variables?.projectRefetch();
    },
    onError: (error: any) => {
      toast(error?.cause as string, { type: "error" });
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["update-answer"],
    onSuccess: (data: any) => {
      if (data.error) {
        throw new Error(data.error?.heading, { cause: data?.error?.message });
      }
      setFetchedQuestionare(questionareData);
      queryClient.setQueryData(["questionare", "APPLICATION_FORM", projectId], {
        data: questionareData,
      });

      toast("Application form saved successfully", { type: "success" });
      const stepCompleted = !questionareData.some(
        (obj: any) => obj.requiredQuestionAnswered === false
      );

      if (stepCompleted) {
        updateProject({
          type: "patch",
          endPoint: `/project/v2/onboarding/${projectId}`,
          data: {
            stepper: {
              ...projectDetails?.stepper,
              applicationForm: "COMPLETED",
            },
          },
          session,
          update,
        });
      } else if (projectDetails?.stepper?.applicationForm === "COMPLETED")
        updateProject({
          type: "patch",
          endPoint: `/project/v2/onboarding/${projectId}`,
          data: {
            stepper: {
              ...projectDetails?.stepper,
              applicationForm: "NOT_STARTED",
            },
          },
          session,
          update,
        });

      if (saveChangesModal) {
        handleCloseChangesModal();
        handleDiscardChangesModal();
      }
    },
    onError: (error: any) => {
      toast(error?.cause as string, { type: "error" });
    },
  });

  const mutateBtn = () =>
    mutate({
      type: "patch",
      endPoint: `/project/v2/onboarding/form/answer?projectId=${projectId}`,
      data: {
        updateAnswer,
      },
      session,
      update,
    });

  useEffect(() => {
    if (submitCurrentStep?.step3) {
      mutateBtn();
      setSubmitCurrentStep({
        ...submitCurrentStep,
        step3: false,
      });
    }
    if (discardStep?.step3) {
      setOpenDiscardModal(true);
    }
    if (nextClicked?.step3) {
      if (
        JSON.stringify(fetchedQuestionare) === JSON.stringify(questionareData)
      ) {
        setSelectedTab(3);
        setNextClicked({
          ...nextClicked,
          step3: false,
        });
      } else setSaveChangesModal(true);
    }
    if (prevClicked?.step3) {
      if (
        JSON.stringify(fetchedQuestionare) === JSON.stringify(questionareData)
      ) {
        setSelectedTab(1);
        setPrevClicked({
          ...prevClicked,
          step3: false,
        });
      } else setSaveChangesModal(true);
    }
  }, [submitCurrentStep, discardStep, nextClicked, prevClicked]);

  const isTab = useMediaQuery("(max-width:1200px)");
  const stickyCheck = useMediaQuery("(max-width:420px)");
  // const isMobileLg = useMediaQuery("(max-width:600px)");

  const currentSection = useRef(0);

  const onClickAction = useCallback(
    (val: number) => {
      const scrollElement = document.getElementById(
        fromApplicationPage ? "scroll-window" : "scroll-area"
      );
      const targetElement = document.getElementById(`Section-${val + 1}`);
      if (targetElement && scrollElement) {
        let headerHeight = stickyCheck ? 330 : isTab ? 230 : 180;
        const targetPosition = targetElement.offsetTop - headerHeight;
        scrollElement.scrollTo({ top: targetPosition, behavior: "smooth" });
      }
    },
    [isTab]
  );

  const handleScroll = useCallback(
    _.debounce(() => {
      const scrollElement = document.getElementById(
        fromApplicationPage ? "scroll-window" : "scroll-area"
      );
      if (scrollElement) {
        const contentSections = document.querySelectorAll(".sections");
        const scrollPosition = scrollElement.scrollTop + window.innerHeight / 2;

        for (let i = 0; i < contentSections.length; i++) {
          const section = contentSections[i] as HTMLElement;
          const sectionTop = section.offsetTop;
          const sectionBottom = sectionTop + section.offsetHeight;

          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            // Only update the state if the section has changed
            if (currentSection.current !== i) {
              currentSection.current = i; // Update the current section ref
              setNextPage(i); // Trigger state update
            }
            break;
          }
        }
      }
    }, 200), // Debounce delay (200ms) to reduce frequent calls
    []
  );

  useEffect(() => {
    const scrollElement = document.getElementById(
      fromApplicationPage ? "scroll-window" : "scroll-area"
    );
    scrollElement?.addEventListener("scroll", handleScroll);
    return () => scrollElement?.removeEventListener("scroll", handleScroll);
  }, []);

  const editData = (
    subsection: string,
    questionId: string,
    newAnswer: string
  ) => {
    setQuestionareData((prevData: any) =>
      prevData.map((sec: any) => {
        let updatedAnsweredQuestions = sec.answeredQuestions;

        const updatedSubsections = sec.subsections.map((subsec: any) => {
          if (subsec.subsection === subsection) {
            const updatedQuestions = subsec.questions.map((question: any) => {
              if (question.id === questionId) {
                // Increment `answeredQuestions` if the new answer is not null and the previous answer was null.
                if (!question.answer && newAnswer) {
                  updatedAnsweredQuestions++;
                }
                // Decrement `answeredQuestions` if the new answer is null and the previous answer was not null.
                if (question.answer && !newAnswer) {
                  updatedAnsweredQuestions--;
                }

                // Compare with the original data and update the `updatedAnswers` state.
                const originalQuestion = data?.data
                  ?.flatMap((sec: any) =>
                    sec.subsections.flatMap((subsec: any) => subsec.questions)
                  )
                  ?.find((q: any) => q.id === questionId);

                if (originalQuestion && originalQuestion.answer !== newAnswer) {
                  setUpdatedAnswers((prev: any) => {
                    const existingEntryIndex = prev.findIndex(
                      (item: any) => item.id === questionId
                    );

                    if (existingEntryIndex > -1) {
                      // Update existing entry
                      const updatedArray = [...prev];
                      updatedArray[existingEntryIndex] = {
                        id: questionId,
                        answer: newAnswer,
                      };
                      return updatedArray;
                    } else {
                      // Add new entry
                      return [...prev, { id: questionId, answer: newAnswer }];
                    }
                  });
                }

                return { ...question, answer: newAnswer };
              }
              return question;
            });

            return { ...subsec, questions: updatedQuestions };
          }
          return subsec;
        });

        // Check if all required questions in the section are answered.
        const allQuestions = updatedSubsections.flatMap(
          (subsec: any) => subsec.questions
        );
        const requiredQuestions = allQuestions.filter(
          (question: any) => question.isRequired
        );

        const allRequiredAnswered =
          requiredQuestions.length === 0 ||
          requiredQuestions.every((question: any) => question.answer);

        return {
          ...sec,
          subsections: updatedSubsections,
          answeredQuestions: updatedAnsweredQuestions,
          requiredQuestionAnswered: allRequiredAnswered,
        };
      })
    );
  };

  const updateQuestionMetadata = (
    id: string,
    updates: { docCount?: number; referenceLinks?: string[] }
  ) => {
    setQuestionMetadata((prevMetadata: any) => {
      const updatedValue = prevMetadata.map((item: any) =>
        item.id === id
          ? {
              ...item,
              docCount: updates.docCount ?? item.docCount,
              referenceLinks: updates.referenceLinks ?? item.referenceLinks,
            }
          : item
      );
      queryClient.setQueryData(
        ["questionareMetaData", "APPLICATION_FORM", projectId],
        updatedValue
      );
      return updatedValue;
    });
  };

  useEffect(() => {
    if (data) {
      let questionIndex = 1; // Initialize the question index counter.

      const newData = data?.data?.map((sec: any) => {
        let updatedAnsweredQuestions = sec.answeredQuestions;
        let allRequiredAnswered = true;

        const allQuestions = sec.subsections.flatMap((subsec: any) =>
          subsec.questions.map((question: any) => ({
            ...question,
            index: questionIndex++, // Assign the index to each question.
            answer: question?.answer || "",
          }))
        );

        const requiredQuestions = allQuestions.filter(
          (question: any) => question.isRequired
        );

        if (requiredQuestions.length === 0) {
          allRequiredAnswered = true;
        } else {
          allRequiredAnswered = requiredQuestions.every(
            (question: any) => question.answer
          );
        }

        // Update subsections with indexed questions.
        const updatedSubsections = sec.subsections.map((subsec: any) => ({
          ...subsec,
          questions: subsec.questions.map((question: any) => ({
            ...question,
            index: allQuestions.find((q: any) => q.id === question.id)?.index,
            answer: question?.answer || "",
          })),
        }));

        return {
          ...sec,
          answeredQuestions: updatedAnsweredQuestions,
          requiredQuestionAnswered: allRequiredAnswered,
          subsections: updatedSubsections,
        };
      });

      // Generate the questionMetadata state.
      const metadata = newData?.flatMap((sec: any) =>
        sec.subsections.flatMap((subsec: any) =>
          subsec.questions.map((question: any) => ({
            id: question.id,
            docCount: question.docCount || 0,
            referenceLinks: question.referenceLinks || [],
          }))
        )
      );

      setQuestionareData(newData);
      setFetchedQuestionare(newData);
      setQuestionMetadata(metadata); // Initialize question metadata state.
    }
  }, [data]);

  useEffect(() => {
    if (setDisableBtn) {
      if (
        JSON.stringify(fetchedQuestionare) === JSON.stringify(questionareData)
      )
        setDisableBtn((prev: any) => ({ ...prev, discard: true, save: true }));
      else
        setDisableBtn((prev: any) => ({
          ...prev,
          discard: false,
          save: false,
        }));
    }
  }, [fetchedQuestionare, questionareData]);

  useEffect(() => {
    if (setIsLoading) setIsLoading(isPending);

    return () => {
      if (setIsLoading) setIsLoading(false);
    };
  }, [isPending]);

  const handleDiscardChangesModal = () => {
    setSelectedTab(
      nextClicked?.step3
        ? 3
        : prevClicked?.step3
          ? 1
          : checkSaveState?.destination
    );
    if (nextClicked?.step3)
      setNextClicked({
        ...nextClicked,
        step3: false,
      });
    else if (prevClicked?.step3)
      setPrevClicked({
        ...prevClicked,
        step3: false,
      });
    setCheckSaveState(null);
  };

  useEffect(() => {
    if (
      checkSaveState &&
      checkSaveState?.current !== checkSaveState?.destination
    ) {
      if (
        JSON.stringify(fetchedQuestionare) === JSON.stringify(questionareData)
      ) {
        setSelectedTab(checkSaveState?.destination);
        setCheckSaveState(null);
      } else setSaveChangesModal(true);
    }
  }, [checkSaveState]);

  useEffect(() => {
    if (metaData) setQuestionMetadata(metaData);
  }, [metaData]);

  return (
    <Grid
      display={"flex"}
      flexDirection={"column"}
      gap={"16px"}
      marginTop={"8px"}
      container
    >
      <Grid item xs={12} lg={9}>
        <Box display={"flex"} flexDirection={"column"} gap={"16px"}>
          {/* <Box
            bgcolor={"#F7F7F7"}
            padding="8px"
            borderRadius={"8px"}
            display={"flex"}
            flexDirection={"column"}
            gap={"4px"}
          >
            <Box display={"flex"} gap={"8px"}>
              <InfoImg />
              <VeTypography variant="label2Secondary">
                You are currently editing this page. When you have completed
                your updates, click Finish Editing to save your changes and
                enable other users to edit the form.
              </VeTypography>
            </Box>
            <Box>
              <VeButton
                buttonVariant="outlined"
                size="small"
                styles={{ width: "fit-content", float: "right" }}
              >
                Finish Editing
              </VeButton>
            </Box>
          </Box> */}
          <Box display={"flex"} flexDirection={"column"} gap={"8px"}>
            <VeTypography variant="subtitle1Main">
              Application Form
            </VeTypography>
            <VeTypography variant="body2Tertiary">
              {!edit
                ? assets?.projectReviewer
                  ? ""
                  : "Mandatory to upload evidence for each answer to provide investors with proof. "
                : `Please answer the questions in each section to provide a clear
              understanding of your project. Ensure that all mandatory questions
              are completed, as they are required to finalize the application
              form and proceed with the next steps.`}
            </VeTypography>
          </Box>
        </Box>
      </Grid>
      {applicationFormLoading ? (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          height={"50vh"}
        >
          <CircularProgress
            sx={{
              color: "var(--vc-violet)",
            }}
          />
        </Box>
      ) : questionareData?.length === 0 ? (
        <Grid item xs={12} lg={9}>
          <Box
            display={"flex"}
            marginBottom={"50px"}
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            paddingTop={"24px"}
            minHeight={"300px"}
          >
            {DynamicImage(assets?.file, "file", 80, 80)}
            <VeTypography marginTop={"12px"} variant="label2StrongerSecondary">
              No questions to display!
            </VeTypography>
          </Box>
        </Grid>
      ) : (
        <Grid item xs={12} lg={12}>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={"16px"}
            paddingBottom={"24px"}
          >
            <Grid
              container
              sx={{
                position: "sticky",
                // top: stickyCheck
                //   ? "196px"
                //   : isMobileLg
                //     ? "140px"
                //     : isTab
                //       ? "108px"
                //       : "64px",
                top:  "0.7px",
                zIndex: 500,
                backgroundColor: "white",
              }}
            >
              <Grid
                item
                xs={12}
                lg={12}
                display={"flex"}
                flexDirection={"column"}
                gap="6px"
              >
                <ApplicationFormTabs
                  data={
                    questionareData?.map((item: any) => ({
                      label: item?.section,
                      questionsLeft: !item?.requiredQuestionAnswered,
                    })) || []
                  }
                  nextPage={nextPage}
                  setNextPage={(val) => {
                    setNextPage(val);
                  }}
                  onClickAction={onClickAction}
                />
                <VeTypography variant="label2Tertiary">
                  Completed:{" "}
                  {questionareData?.reduce(
                    (acc: number, ans: any) => acc + ans?.answeredQuestions,
                    0
                  )}
                  /
                  {questionareData?.reduce(
                    (acc: number, ques: any) => acc + ques?.totalQuestions,
                    0
                  )}
                </VeTypography>
              </Grid>
            </Grid>
            {questionareData?.map((sectionData: any, index: number) => (
              <Box
                key={sectionData.section}
                // ref={(el) => (sectionRefs.current[sectionData.section] = el)}
                id={`Section-${index + 1}`}
                className="sections"
                data-section={sectionData.section}
              >
                <Box
                  borderBottom="1px solid #D5DBE1"
                  paddingBottom={"8px"}
                  marginBottom={"16px"}
                >
                  <VeTypography variant="body1StrongerSecondary">
                    {cleanString(sectionData.section)}
                  </VeTypography>
                </Box>
                {sectionData?.subsections?.map((item: any, index: number) => (
                  <SubSection
                    data={item}
                    RichText={RichText}
                    editData={editData}
                    key={index}
                    assets={assets}
                    DynamicImage={DynamicImage}
                    getFileTypeImage={getFileTypeImage}
                    useSession={useSession}
                    useMutation={useMutation}
                    useQueryData={useQueryData}
                    mutateAction={mutateAction}
                    toast={toast}
                    projectId={projectId}
                    updateQuestionMetadata={updateQuestionMetadata}
                    questionMetadata={questionMetadata}
                    timeZoneDateTimeUser={timeZoneDateTimeUser}
                    setProjectDetails={setProjectDetails}
                    projectDetails={projectDetails}
                    edit={edit}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Grid>
      )}
      <SuccessModal
        width="436px"
        open={openDiscardModal}
        onClose={handleCloseDiscardModal}
        title="Discard changes?"
        desc="There are unsaved changes in this step that will be lost if you don’t save them."
        containedButtonTitle="Yes"
        outlinedButtonTitle="No"
        outlinedOnClick={handleCloseDiscardModal}
        containedOnClick={() => {
          setQuestionareData(fetchedQuestionare);
          setDiscardStep({
            ...discardStep,
            step3: false,
          });
          handleCloseDiscardModal();
        }}
        iconSvg={<WarningSvg />}
        styleColor="25855A"
      />
      <SuccessModal
        open={saveChangesModal}
        onClose={handleCloseChangesModal}
        title="Save recent changes?"
        desc="There are unsaved changes in this step that will be lost if you don’t save them."
        containedButtonTitle="Save Changes"
        outlinedButtonTitle="Discard"
        outlinedOnClick={handleDiscardChangesModal}
        loading={isPending}
        containedOnClick={mutateBtn}
        iconSvg={<WarningSvg />}
        styleColor="25855A"
      />
    </Grid>
  );
}
